import React, { useEffect, useRef, useState } from 'react';

const Carousel = ({
  children,
  interval,
  autoPlay,
  displayArrow,
  animationType // 'fade' | 'slide' | 'zoom' | 'flip' | 'drop' | 'push'
}) => {
  const total = React.Children.count(children);
  const [index, setIndex] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [progress, setProgress] = useState(0);
  const animationRef = useRef(null);
  const startTimeRef = useRef(null);

  const next = () => {
    setIndex((prev) => (prev + 1) % total);
    setProgress(0);
    startTimeRef.current = null;
  };

  const prev = () => {
    setIndex((prev) => (prev - 1 + total) % total);
    setProgress(0);
    startTimeRef.current = null;
  };

  const goTo = (i) => {
    setIndex(i);
    setProgress(0);
    startTimeRef.current = null;
  };

  useEffect(() => {
    if (!autoPlay || hovered || total <= 1) return;

    const animate = (timestamp) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const elapsed = timestamp - startTimeRef.current;

      const progressPercent = Math.min((elapsed / interval) * 100, 100);
      setProgress(progressPercent);

      if (elapsed >= interval) {
        next();
      } else {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationRef.current);
  }, [hovered, index, autoPlay, interval, total]);

  return (
    total > 1 ? (
      <div
        className="carousel-wrapper"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <style>{`
          .carousel-wrapper {
            width: 100%;
            overflow: hidden;
            font-family: sans-serif;
            position: relative;
            box-shadow: 0 4px 20px rgba(0,0,0,0.1);
          }

          .carousel-inner.fade,
          .carousel-inner.zoom,
          .carousel-inner.flip,
          .carousel-inner.drop,
          .carousel-inner.push {
            position: relative;
            width: 100%;
            height: 100%;
          }

          .carousel-inner.slide {
            display: flex;
            width: ${total * 100}%;
            transform: translateX(-${index * (100 / total)}%);
            transition: transform 0.6s ease-in-out;
          }

          .carousel-slide.fade,
          .carousel-slide.zoom,
          .carousel-slide.flip,
          .carousel-slide.drop,
          .carousel-slide.push {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            opacity: 0;
            transition: all 1s ease-in-out;
            z-index: 0;
          }

          .carousel-slide.fade.active {
            opacity: 1;
            z-index: 1;
            position: relative;
          }

          .carousel-slide.zoom {
            transform: scale(0.9);
          }

          .carousel-slide.zoom.active {
            opacity: 1;
            transform: scale(1);
            z-index: 1;
            position: relative;
          }

          .carousel-slide.slide {
            flex: 0 0 ${100 / total}%;
            width: ${100 / total}%;
          }

          .carousel-slide.flip {
            transform: rotateY(90deg);
          }

          .carousel-slide.flip.active {
            transform: rotateY(0deg);
            opacity: 1;
            z-index: 1;
            position: relative;
          }

          .carousel-slide.drop {
            transform: translateY(-100%);
          }

          .carousel-slide.drop.active {
            transform: translateY(0);
            opacity: 1;
            z-index: 1;
            position: relative;
          }

          .carousel-slide.push {
            transform: translateX(100%);
          }

          .carousel-slide.push.active {
            transform: translateX(0);
            opacity: 1;
            z-index: 1;
            position: relative;
          }

          .carousel-btn {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            background: rgba(0, 0, 0, 0.5);
            border: none;
            color: white;
            font-size: 24px;
            padding: 8px 12px;
            cursor: pointer;
            z-index: 10;
            border-radius: 4px;
            opacity: 0;
            transition: opacity 0.3s ease;
          }

          .carousel-wrapper:hover .carousel-btn {
            opacity: 1;
          }

          .carousel-btn-display {
            opacity: 1;
          }

          .carousel-btn.left {
            left: 10px;
          }

          .carousel-btn.right {
            right: 10px;
          }

          .carousel-btn:hover {
            background: rgba(0, 0, 0, 0.7);
          }

          .carousel-dots {
            text-align: center;
            margin-top: 10px;
          }

          .carousel-dot {
            display: inline-block;
            height: 6px;
            width: 15px;
            margin: 0 4px;
            background-color: #ccc;
            border-radius: 3px;
            cursor: pointer;
            position: relative;
            overflow: hidden;
            transition: width 0.3s ease;
          }

          .carousel-dot.active {
            width: 40px;
            background-color: #ccc;
          }

          .dot-progress {
            position: absolute;
            top: 0;
            left: 0;
            height: 100%;
            background-color: #333;
            transition: width 0.1s linear;
          }
        `}</style>

        <button className={displayArrow ? "carousel-btn carousel-btn-display left" : "carousel-btn left"} onClick={prev}>‹</button>
        <button className={displayArrow ? "carousel-btn carousel-btn-display right" : "carousel-btn right"} onClick={next}>›</button>

        <div className={`carousel-inner ${animationType}`}>
          {React.Children.map(children, (child, i) => (
            <div
              key={i}
              className={`carousel-slide ${animationType} ${i === index ? 'active' : ''}`}
            >
              {child}
            </div>
          ))}
        </div>

        <div className="carousel-dots">
          {Array.from({ length: total }).map((_, i) => (
            <span
              key={i}
              className={`carousel-dot ${i === index ? 'active' : ''}`}
              onClick={() => goTo(i)}
            >
              {i === index && (
                <div className="dot-progress" style={{ width: `${progress}%` }} />
              )}
            </span>
          ))}
        </div>
      </div>
    ) : (
      React.Children.map(children, (child, i) => <div key={i}>{child}</div>)
    )
  );
};

export default Carousel;